<template>
  <div ref="scrollElement" class="page-academy wh-full overflow-auto">
    <div class="page-academy-banner flex-col justify-center items-center">
      <nuxt-img
        class="page-academy-banner__img"
        fetchPriority="hightest"
        :src="transformImage(pageBackground)"
      ></nuxt-img>
      <h1 class="mb-[16px]">
        <span :class="isChinese() ? 'text-gradient' : 'text-white'">
          {{ pageTitle }}
        </span>
      </h1>
      <p :class="['font-[500]', isChinese() ? 'text-[#0d93fa]' : 'text-white']">
        {{ pageSubTitle }}
      </p>
    </div>
    <div :class="'sticky-active'">
      <div v-if="displayCategory.length > 3" :class="['top-nav']">
        <div v-for="(item, idx) in displayCategory" :key="item.id">
          <a
            :class="['top-nav-item', { active: currentVisible === idx }]"
            @click="handleScrollTo(idx)"
          >
            {{ item.title }}
          </a>
        </div>
      </div>
    </div>
    <article class="page-academy-content mx-auto">
      <section
        v-for="item in displayCategory"
        :id="item.title"
        :key="item.id"
        :class="[
          'page-academy-content__group mb-[28px]',
          item.jump_url.replaceAll('/', ''),
        ]"
      >
        <component
          :is="displayCard(item)"
          :isAcademy="true"
          :category="item"
        ></component>
      </section>
    </article>
  </div>
</template>

<script lang="ts" setup>
import CategoryDoc from './category-doc.vue'
import CategoryLive from './category-live.vue'
import CategoryBrand from './category-brand.vue'
import CategoryTutorial from './category-tutorial.vue'
import CategoryHighlight from './category-highlight.vue'

import { PAGE_CARD_TYPE } from '~/api/interface/publicize'
import { getPromotionCenterPageCard } from '~/api/module/publicize'
import {
  getHelpCategory,
  SEARCH_TYPE,
  type IHelpCategory,
} from '~/api/module/help-center'

const { $t } = useNuxtApp()

const currentVisible = ref<number>(0)

const onScroll = () => {
  const list: number[] = []
  document
    .querySelectorAll<HTMLDivElement>('.page-academy-content__group')
    .forEach((item) => {
      list.push(item.getBoundingClientRect().top - 170)
    })

  const targetIndex = list.findIndex((item) => item > 0)

  currentVisible.value = targetIndex < 1 ? 0 : targetIndex - 1
}

const handleScrollTo = (idx: number) => {
  const list = document.querySelectorAll<HTMLDivElement>(
    '.page-academy-content__group'
  )
  if (scrollElement.value) {
    scrollElement.value.scrollTop =
      scrollElement.value.scrollTop +
      list[idx].getBoundingClientRect().top -
      150
  }
}

onMounted(() => {
  scrollElement.value?.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  scrollElement.value?.removeEventListener('scroll', onScroll)
})

const scrollElement = ref<HTMLDivElement>()

const displayCard = (data: IHelpCategory) => {
  const isLive = data.title.includes($t('zhi-bo'))
  if (isLive) {
    return CategoryLive
  }
  switch (data.jump_url) {
    case '/case':
    case 'case':
      return CategoryBrand
    case 'tutorial':
    case '/tutorial':
      return CategoryTutorial
    case 'highlight':
    case '/highlight':
      return CategoryHighlight
    default:
      return CategoryDoc
  }
}

const { data: categoryData } = useAsyncData(async () => {
  const res = await getHelpCategory({ model_type: SEARCH_TYPE.ACADEMY })
  return res.data
})

const { data: pageData } = useAsyncData(async () => {
  const res = await getPromotionCenterPageCard({
    type: PAGE_CARD_TYPE.ACADEMY,
  })
  return res.data
})

const displayCategory = computed(() => {
  return (
    categoryData.value?.filter(
      (item) => (item.magic_school_detail || [])?.length > 0 || item.jump_url
    ) || []
  )
})

const pageTitle = computed(() => pageData.value?.[0].title)
const pageSubTitle = computed(() => pageData.value?.[0].sub_title)
const pageBackground = computed(() => pageData.value?.[0].cover_url)
</script>

<style lang="scss" scoped>
.page-academy-content__group {
  &.tutorial {
    margin-bottom: 40px;
  }
  &.highlight {
    margin-bottom: 40px;
  }
}

:deep(h2) {
  margin-top: 0;
}

.sticky-hidden {
  position: absolute;
  transform: translateY(-100%);
  transition: all 0.3s ease-out;
}
.sticky-active {
  position: sticky;
  top: 40px;
  transform: translateY(0%);
  transition: all 0.3s ease-out;
  width: 100%;
  margin: 40px auto;
  height: 48px;
  pointer-events: none;
  z-index: var(--layer-sticky);
  .top-nav {
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.122);
    pointer-events: initial;
    position: absolute;
    display: flex;
    background-color: var(--back-bg);
    left: 50%;
    transform: translateX(-50%);
    z-index: 22;
    align-items: center;
    justify-content: center;
  }
}
.top-nav {
  height: 48px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0px 4px 20px 0px rgba(255, 255, 255);
  border: 1px solid var(--back-5);
  display: none;
  padding: 15px 32px;
  gap: 24px;
  .top-nav-item {
    width: 100px;
    font-size: 12px;
    color: var(--text-1);
    &.active {
      font-weight: 600;
    }
  }
}
.page-academy {
  height: calc(100vh - 56px);
  display: block !important;
  &-banner {
    position: relative;
    height: 266px;
    gap: 16px;
    &__img {
      position: absolute;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    h1,
    p {
      position: relative;
      z-index: 1;
    }
    h1 {
      font-size: 48px;
      margin: 0;
      .text-gradient {
        background-image: linear-gradient(to right, #ef17fd, #05d4ff);
        -webkit-background-clip: text;
        color: transparent;
      }
    }
    p {
      margin: 0;
      font-size: 20px;
    }
  }
  &-content {
    width: 1200px;
    padding: 0 0 40px;
  }
}
</style>
